import { ManualTriggerInput } from '@/types/automationSteps.ts';
import { TriggerDataset } from '@/types/datasets.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import {
  analyzeTriggerDatasetCompatibility,
} from '@/pages/AutomationPage/components/ManualRunModal/utils/triggerDatasetMatching.ts';

export type RunGroup = {
  key: string;
  runs: RunWithDatasets[];
  latest: RunWithDatasets;
  count: number;
  matchedInputs: TriggerDataset[];
  matchedTriggerCount: number;
  missingRequiredTriggers: ManualTriggerInput[];
  missingOptionalTriggers: ManualTriggerInput[];
  extraInputCount: number;
};

const makeDatasetKey = (inputDatasets: TriggerDataset[]) =>
  inputDatasets.map((dataset) => `${dataset.trigger_id ?? ''}:${dataset.dataset.id}`).join('|');

const sortRunsNewestFirst = (a: RunWithDatasets, b: RunWithDatasets) =>
  new Date(b.run.created_at).getTime() - new Date(a.run.created_at).getTime();

export const groupMatches = (matches: RunGroup[], sortByMatchCount = false) => {
  const groupsByKey = new Map<string, RunGroup[]>();

  for (const match of matches) {
    const group = groupsByKey.get(match.key) ?? [];
    group.push(match);
    groupsByKey.set(match.key, group);
  }

  const grouped = Array.from(groupsByKey.values(), (groupsForKey) => {
    const runs = groupsForKey.flatMap((group) => group.runs).sort(sortRunsNewestFirst);
    const latest = runs[0];
    const representative = groupsForKey[0];

    return {
      ...representative,
      runs,
      latest,
      count: runs.length,
    };
  });

  grouped.sort((a, b) => {
    if (sortByMatchCount && a.matchedTriggerCount !== b.matchedTriggerCount) {
      return b.matchedTriggerCount - a.matchedTriggerCount;
    }

    return sortRunsNewestFirst(a.latest, b.latest);
  });

  return grouped;
};

export const buildRunMatch = (
  triggers: ManualTriggerInput[],
  run: RunWithDatasets,
): RunGroup | null => {
  const compatibility = analyzeTriggerDatasetCompatibility({
    triggers,
    inputDatasets: run.input_datasets,
  });

  if (compatibility.matchType === 'none') {
    return null;
  }

  return {
    key: [
      makeDatasetKey(compatibility.matchedInputs),
      `missing:${[...compatibility.missingRequiredTriggers, ...compatibility.missingOptionalTriggers]
        .map((trigger) => trigger.output_id)
        .join(',')}`,
      `extra:${compatibility.extraInputCount}`,
    ].join('::'),
    runs: [run],
    latest: run,
    count: 1,
    matchedInputs: compatibility.matchedInputs,
    matchedTriggerCount: compatibility.matchedTriggerCount,
    missingRequiredTriggers: compatibility.missingRequiredTriggers,
    missingOptionalTriggers: compatibility.missingOptionalTriggers,
    extraInputCount: compatibility.extraInputCount,
  };
};

export const isPerfectRunMatch = (match: RunGroup) =>
  match.missingRequiredTriggers.length === 0 && match.extraInputCount === 0;
